import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { ForgotPasswordScreen } from "./auth/ForgotPasswordScreen";
import { InstallPromptModal } from "./modals/InstallPromptModal";
import { PublicAuraChat } from "./about/PublicAuraChat";

export function WelcomeScreen() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('welcome'); // welcome, signup, login, forgot
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        if (!agreed) {
          setError('Please agree to the trial terms');
          setSubmitting(false);
          return;
        }
        await register(email, password, name);
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === 'forgot') {
    return <ForgotPasswordScreen onBack={() => setMode('login')} />;
  }

  if (mode === 'welcome') {
    return (
      <div className="app-container">
        <div className="init-screen welcome-landing" data-testid="welcome-landing">
          <div className="init-content welcome-landing-content">
            {/* Phase 30.L — Big AURA wordmark with a smaller ".Beta" suffix.
                Founder ask: "make Aura's name bigger and put a smaller sized
                '.Beta'". The .Beta tag inherits the gradient via the parent
                background-clip; sized at ~28% of the wordmark. */}
            <h1 className="aura-mega-title" data-testid="aura-mega-title">
              AURA<span className="aura-mega-beta">.Beta</span>
            </h1>
            <p className="aura-mega-tagline">Your personal AI companion that lives as your wallpaper, learning and growing with you.</p>

            {/* Public Aura chat — drop straight in so visitors can try her
                before signing up. Themed to match the Beta UI via App.css. */}
            <div className="welcome-public-chat-wrap" data-testid="welcome-public-chat-wrap">
              <PublicAuraChat />
            </div>

            <div className="welcome-cta-row" data-testid="welcome-cta-row">
              <button
                className="init-btn primary-btn"
                onClick={() => setShowInstallPrompt(true)}
                data-testid="signup-cta-btn"
              >
                Start Your Free Trial
              </button>
              <button
                className="ghost-btn"
                onClick={() => setMode('login')}
                data-testid="login-cta-btn"
              >
                I already have an account
              </button>
            </div>

            {/* Marquee — minimalist "Coming soon..." scroll. No box, no
                feature names (they live in #about). Tapping opens AboutPage. */}
            <a
              href="#about"
              className="coming-soon-marquee"
              data-testid="coming-soon-marquee"
              aria-label="See what's coming soon to Aura"
            >
              <span className="coming-soon-marquee-track">
                <span className="coming-soon-marquee-inner">
                  ✦ Coming soon…&nbsp;&nbsp;&nbsp;✦ Coming soon…&nbsp;&nbsp;&nbsp;✦ Coming soon…&nbsp;&nbsp;&nbsp;✦ Coming soon…&nbsp;&nbsp;&nbsp;
                </span>
                <span className="coming-soon-marquee-inner" aria-hidden="true">
                  ✦ Coming soon…&nbsp;&nbsp;&nbsp;✦ Coming soon…&nbsp;&nbsp;&nbsp;✦ Coming soon…&nbsp;&nbsp;&nbsp;✦ Coming soon…&nbsp;&nbsp;&nbsp;
                </span>
              </span>
            </a>

            {/* Larger, easier-to-find legal/about nav.
                Founder ask: "have the terms and other buttons bigger so
                people can find them". */}
            <div className="welcome-big-nav" data-testid="welcome-big-nav">
              <a href="#about"   className="welcome-big-nav-btn" data-testid="welcome-nav-about">About</a>
              <a href="#privacy" className="welcome-big-nav-btn" data-testid="welcome-nav-privacy">Privacy</a>
              <a href="#terms"   className="welcome-big-nav-btn" data-testid="welcome-nav-terms">Terms</a>
              <a href="#refund"  className="welcome-big-nav-btn" data-testid="welcome-nav-refund">Refunds</a>
            </div>
          </div>

          {/* SVG gradient defs for BetaIcon strokes when on the welcome screen
              (MainApp also defines this, but the welcome screen renders before
              MainApp ever mounts). */}
          <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
            <defs>
              <linearGradient id="beta-gradient-stroke" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#ec4899" />
                <stop offset="100%" stopColor="#9333ea" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        {showInstallPrompt && (
          <InstallPromptModal
            onContinue={() => { setShowInstallPrompt(false); setMode('signup'); }}
            onClose={() => setShowInstallPrompt(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="init-screen">
        <div className="init-content auth-form">
          <h2 className="auth-title">
            {mode === 'signup' ? 'Start Your Free Trial' : 'Welcome Back'}
          </h2>

          <button
            className="google-btn"
            onClick={handleGoogleLogin}
            data-testid="google-auth-btn"
          >
            <span className="google-icon">G</span>
            Continue with Google
          </button>

          <div className="divider"><span>or</span></div>

          {mode === 'signup' && (
            <input
              type="text"
              className="init-input"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="name-input"
            />
          )}

          <input
            type="email"
            className="init-input"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="email-input"
          />

          <input
            type="password"
            className="init-input"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="password-input"
          />

          {mode === 'signup' && (
            <div className="trial-agreement" data-testid="trial-agreement">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  data-testid="agreement-checkbox"
                />
                <span>
                  I agree to the 7-day free trial. After the trial ends, I'll be prompted to pay <strong>$4.99</strong> to continue. I can cancel anytime before the trial ends.<br />
                  By signing up, I accept the <a href="#terms" target="_blank" rel="noopener noreferrer" data-testid="signup-terms-link">Terms of Service</a> and <a href="#privacy" target="_blank" rel="noopener noreferrer" data-testid="signup-privacy-link">Privacy Policy</a>.
                </span>
              </label>
            </div>
          )}

          {error && <div className="error-message" data-testid="auth-error">{error}</div>}

          <button
            className="init-btn"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="auth-submit-btn"
          >
            {submitting ? '...' : (mode === 'signup' ? 'Start Trial' : 'Login')}
          </button>

          <button
            className="ghost-btn small"
            onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')}
            data-testid="mode-toggle-btn"
          >
            {mode === 'signup' ? 'Already have an account? Login' : "Don't have an account? Sign up"}
          </button>

          {mode === 'login' && (
            <button
              className="ghost-btn small"
              onClick={() => setMode('forgot')}
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
          )}

          <p className="welcome-promo-hint" data-testid="welcome-promo-hint">
            Have a promo code? You can redeem it in Settings after signing in.
          </p>

          <button
            className="ghost-btn small"
            onClick={() => setMode('welcome')}
            data-testid="back-btn"
          >
            ← Back
          </button>
        </div>
        <footer className="welcome-footer">
          <a href="#about" data-testid="footer-about">About</a>
          <span>·</span>
          <a href="#privacy" data-testid="footer-privacy">Privacy</a>
          <span>·</span>
          <a href="#terms" data-testid="footer-terms">Terms</a>
          <span>·</span>
          <a href="#refund" data-testid="footer-refund">Refunds</a>
        </footer>
      </div>
    </div>
  );
}
